import { BusinessUnitModel } from "../../../../../models/customers/customer.models";
import { BuPortConfigModel, BuPortFormConfigModel, SaveBuPortConfigModel } from "../../ports-and-terminals/models/ports-and-terminals.models";
import * as Enumerable from "linq";
import { BindingSignaler } from "aurelia-templating-resources";
import { PortsAndTerminalsService } from "../../ports-and-terminals/services/ports-and-terminals.service";
import { autoinject } from "aurelia-dependency-injection";
import { EntityTypeConfigurationHelper, EntityTypeConfigurationModel } from "../../../../../models/basics/basics.models";
import { NotificationClientService, NotificationType } from "../../../../../services/basics/notification-client.service";
import { RulesClientService } from "../../../../../services/rules/rules-client.service";
import { GetBuRuleCollectionModel } from "../../../../../models/rules/rules.models";
import { VettingType } from "../../../../../models/vetting/vetting.models";
import { ValidationController, ValidationRules } from "aurelia-validation";
import { CustomValidationRenderer } from "../../../../../client-validation-system/custom-validation-renderer";
import { useView } from "aurelia-framework";

@autoinject()
@useView('views/pages/modules/business-unit/tabs/business-unit-ports-and-terminals.html')
export class BusinessUnitPortsAndTerminals {

    model: BusinessUnitModel;
    ports: BuPortConfigModel[] = [];
    currentlySelectedPortConfig;
    // Displayed text within 'Port Name' entity-finder
    portSearchFilterText: string;

    public portEntityTypes: EntityTypeConfigurationModel[];
    ruleCollections: GetBuRuleCollectionModel[] = [];

    constructor(private portsAndTerminalsService: PortsAndTerminalsService, private bindingSignaler: BindingSignaler,
        private entityTypeConfigurationHelper: EntityTypeConfigurationHelper, private notificationClientService: NotificationClientService,
        private ruleService:RulesClientService, private validationController: ValidationController) {
            this.validationController.addRenderer(new CustomValidationRenderer());
            this.portEntityTypes = this.entityTypeConfigurationHelper.getConfigurationWithoutPermissions(["Ports"]);
    }

    async activate(model: BusinessUnitModel) {
        this.model = model;
        this.ruleCollections = await this.ruleService.getBuRuleCollections(this.model.businessUnitId, VettingType.AutoScreening, true);
        await this.loadPorts();
    }

    async loadPorts() {
        if(this.validationController)
            this.validationController.reset();
        this.ports = await this.portsAndTerminalsService.getBuPortConfig(this.model.businessUnitId);
        this.ports.forEach((port: BuPortConfigModel) => {
            port.forms.forEach((form: BuPortFormConfigModel) => {
                form.originalOnboardingConfigId = form.onboardingConfigId;
                form.originalFormValidityPeriodInDays = form.formValidityPeriodInDays;
                form.isDeleted = false;
            });

            this.addPortValidations(port);
        });
        this.ports = this.ports.reverse();

        this.portsChangedHandler();
    }

    /**
    * Triggered when a port gets added/removed
    */
    portsChangedHandler = () => {
        this.ports.forEach((port: BuPortConfigModel) => {
            this.onPortFormsChanged(port);
        });
    }

    /**
    * Triggers when a form gets added/modified/deleted
    * @param port
    */
    onPortFormsChanged = (port: BuPortConfigModel) => {
        port.formIds = port.forms
            .filter((port: BuPortFormConfigModel) => !port.isDeleted)
            .map((form: BuPortFormConfigModel) => form.onboardingConfigId);
    }

    /**
    * Triggered when an existing port form is changed in the View
    * @param port The newly selected port
    */
    public onPortSelected(port) {
        if (!port) return;

        const alreadyAdded = port?.bKey && this.ports?.findIndex(x => x.portBKey == port.bKey) > -1;
        if (!alreadyAdded) {
            this.currentlySelectedPortConfig = { portBKey: port.bKey, portName: port.name, countryName: port.countryName, isNew: true,
                hasTimeZone: port.timezoneOffset && port.timezoneOffset.trim() != '', forms: [{ formValidityPeriodInDays: null }] };
        }
        else {
            this.notificationClientService.show('Port already added', `Port ${port.name}, ${port.countryName} is already added`, NotificationType.Info);
            //Clear port entity search
            setTimeout(() =>{
                this.portSearchFilterText = "";
            }, 0)
        }
    }

    /**
    * Add currently selected port to existing list of ports
    */
    addPort(){
        if (this.currentlySelectedPortConfig) {
            if(this.validateNewPort())
            {
                this.ports.unshift(this.currentlySelectedPortConfig);
                this.currentlySelectedPortConfig = undefined;
                this.portsChangedHandler();
                this.portSearchFilterText = "";
                this.addPortValidations(this.ports[0]);
            }
        }
        else {
            this.notificationClientService.show('No port selected', `Please select port to add`, NotificationType.Info);
        }
    }

    /**
    * Remove port from Business Unit
    * @param portDeleted Port to be deleted
    */
    removePort(portDeleted: BuPortConfigModel) {
        if (this.ports && portDeleted) {
            const index = this.ports.filter(port => !port.isDeleted).findIndex(port => port == portDeleted);

            //If the deleted port has forms, and is not new (exists in DB)
            if (portDeleted.forms.filter(x => x.formConfigMemberId > 0).length > 0 || !portDeleted.isNew) {
                portDeleted.isDeleted = true;
                portDeleted.forms.forEach((form: BuPortFormConfigModel) => form.isDeleted = true);
            }
            else this.ports.splice(index, 1);

            this.bindingSignaler.signal("port-removed");

            this.portsChangedHandler();
        }
    }

    /**
    * validate new port being added
    */
    validateNewPort(): boolean
    {
        const port = this.currentlySelectedPortConfig;

        if (port && !port.hasTimeZone) 
        {
            this.notificationClientService.show('Cannot add port', `The port selected does not have timezone configured.`, NotificationType.Error);
            return false;
        }

        return true;
    }

    /**
    * Save port configuration
    */
    public async savePortConfig() {
        const modifiedPortConfigs = this.getModifiedPortConfigs();
        const buPortConfigs = Enumerable.from(modifiedPortConfigs).selectMany((modifiedPortConfig: BuPortConfigModel) => {
            const modifiedConfigs: SaveBuPortConfigModel[] = [];

            //If port was deleted
            if (modifiedPortConfig.isDeleted) {
                modifiedConfigs.push({
                    portConfigMemberId: null,
                    portBKey: modifiedPortConfig.portBKey,
                    onboardingConfigId: null,
                    isDelete: true,
                    formValidityPeriodInDays: null,
                    ruleCollectionId: null
                });
            }

            //Get port modified forms
            const modifiedPortForms = modifiedPortConfig.isModified?  modifiedPortConfig.forms: modifiedPortConfig.forms?.length ? this.getModifiedFormConfigs(modifiedPortConfig.forms, modifiedPortConfig.isNew) : [];

            // Note: If a form has been modified, two entries are added to the 'modifiedConfigs'
            // One entry is to delete the current form, and the second one to add the modified form (replace old form with modified)
            modifiedConfigs.push(...modifiedPortForms.map((modifiedPortForm: BuPortFormConfigModel) => {
                return {
                    portConfigMemberId: modifiedPortForm.formConfigMemberId,
                    portBKey: modifiedPortConfig.portBKey,
                    onboardingConfigId: modifiedPortForm.formConfigMemberId > 0 ? modifiedPortForm.originalOnboardingConfigId : modifiedPortForm.onboardingConfigId,
                    onboardingConfigName: modifiedPortForm.onboardingConfigName,
                    formValidityPeriodInDays: modifiedPortForm.formValidityPeriodInDays,
                    ruleCollectionId: modifiedPortConfig.ruleCollectionId,
                    isDelete: modifiedPortForm.isDeleted ||
                        (modifiedPortForm.originalOnboardingConfigId != modifiedPortForm.onboardingConfigId
                            && modifiedPortForm.isModified) //Modified forms are set as deleted and then new row added (for history)
                };
            }));

            //Adding new entry for modified forms
            modifiedConfigs.push(...modifiedPortForms
                .filter((modifiedPortForm: BuPortFormConfigModel) =>  modifiedPortForm.isModified && modifiedPortForm.originalOnboardingConfigId != modifiedPortForm.onboardingConfigId)
                .map((modifiedPortForm: BuPortFormConfigModel) => {
                    return {
                        portConfigMemberId: null,
                        portBKey: modifiedPortConfig.portBKey,
                        onboardingConfigId: modifiedPortForm.onboardingConfigId,
                        onboardingConfigName: modifiedPortForm.onboardingConfigName,
                        formValidityPeriodInDays: modifiedPortForm.formValidityPeriodInDays,
                        ruleCollectionId: modifiedPortConfig.ruleCollectionId,
                        isDelete: false
                    };
                })
            );

            return modifiedConfigs;
        }).toArray();

        await this.portsAndTerminalsService.saveBuPortConfig({
            portConfigs: buPortConfigs,
            businessUnitId: this.model.businessUnitId,
            departureDaysFromArrival: this.model.portsAndTerminalsDepartureDaysFromArrival
        });

        //Reload ports, to load 'formConfigMemberId' into Port forms, as new forms will have that field empty
        await this.loadPorts();

    }

    /**
    * Check if any port configuration changes were made
    */
    public hasPortConfigChanges(): boolean {
        return this.getModifiedPortConfigs().length > 0;
    }

    /**
    * Returns list of all modified Port configs
    * @private
    */
    private getModifiedPortConfigs() {
        if (!this.ports?.length) return [];

        return this.ports.filter((port: BuPortConfigModel) =>
            port.isDeleted || port.isModified ||
            this.getModifiedFormConfigs(port.forms, port.isNew).length > 0
        );
    }

    onRuleCollectionIdChanged(port){
        port.isModified = true;
    }

    /**
    * Returns a list of modified Forms belonging to a Port
    * @param forms List of port forms
    * @param isNewPort Is port new?
    * @private
    */
    private getModifiedFormConfigs(forms: BuPortFormConfigModel[], isNewPort: boolean) {
        return forms.filter((form: BuPortFormConfigModel) =>
            //new ports without forms or modified or deleted forms
            ((!form.formConfigMemberId || form.formConfigMemberId <= 0) && !form.isDeleted && (form.onboardingConfigId > 0 || isNewPort))
            || (form.onboardingConfigId > 0 && (form.isDeleted || form.isModified))
        );
    }

    /**
    * Remove a form from a port
    * @param port Port containing the removed form
    * @param form The removed form
    */
    removeForm(port: BuPortConfigModel, removedForm: BuPortFormConfigModel) {
        if (port?.forms && removedForm != null) {
            const index = port.forms.filter(form => !form.isDeleted).findIndex(form => form == removedForm);

            if (removedForm.formConfigMemberId > 0) {
                removedForm.isDeleted = true;
            }
            else port.forms.splice(index, 1);

            this.bindingSignaler.signal("form-removed");

            this.onPortFormsChanged(port);
        }
    }

    /**
    * Add form to existing port
    * @param port Port
    */
    addFormToPort(port: BuPortConfigModel) {
        port.forms.push({ formValidityPeriodInDays: null });
    }

    /**
    * Triggered when an existing port form is changed in the View
    * @param event
    * @param port Port
    * @param index Form index (within port forms)
    */
    onPortFormChanged(event, port: BuPortConfigModel, currentFormConfig: BuPortFormConfigModel) {
        if (currentFormConfig && event?.detail?.item) {
            const newFormConfig: { onboardingConfigId: number, name: string } = {
                onboardingConfigId: event.detail.item.id,
                name: event.detail.item.name
            };

            //If the current form has just been added to the port (did not exist in DB)
            if (!currentFormConfig.originalOnboardingConfigId) {
                currentFormConfig.originalOnboardingConfigId = event.detail.oldItem ?? null;
            }

            currentFormConfig.onboardingConfigName = newFormConfig.name;

            //If the new form config's onBoardingId is not equal to the current onBoardingId, Port is not new, and changed Form did not exist previously in DB
            if (!currentFormConfig.isModified)
                currentFormConfig.isModified = newFormConfig.onboardingConfigId !== currentFormConfig.originalOnboardingConfigId && !port.isNew && currentFormConfig.formConfigMemberId > 0;

            this.onPortFormsChanged(port);
        }
    }

    onPortFormValidityChanged(event, port: BuPortConfigModel, currentFormConfig: BuPortFormConfigModel) {
        if (currentFormConfig && event?.detail?.newValue) {
            const newFormValidityPeriodInDays = Number(event.detail.newValue);

            currentFormConfig.formValidityPeriodInDays = newFormValidityPeriodInDays;
            //need to set isModified if not new port config and not new form config
            currentFormConfig.isModified = !port.isNew && currentFormConfig.formConfigMemberId > 0 && currentFormConfig.originalFormValidityPeriodInDays !== newFormValidityPeriodInDays;
        }
    }

    addPortValidations(port: BuPortConfigModel)
    {
        ValidationRules
            .ensure<BuPortConfigModel, number>(x => x.ruleCollectionId)
            .displayName('Rule collection')
            .required()
            .on(port);
    }

    async isValid(): Promise<boolean>
    {
        const result = await this.validationController.validate();

        return result.valid;
    }
}
